import gql from "graphql-tag";

import type {LoginUserData} from "@khan/auth-shared";
import * as Cookies from "@khan/data-cookies";
import type {ApolloGqlClient} from "@khan/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {Errors, KAError} from "@khan/logging";

import type {
    loginWithCleverMutationType,
    transferCleverAuthMutationType,
} from "./__graphql-types__/index.ts";

interface CleverMutationData {
    user: Nullable<LoginUserData>;
    isLibraryUser: Nullable<boolean>;
    transferToken?: string;
    isFirstLogin: Nullable<boolean>;
    error: Nullable<
        | {
              code: "INVALID_TOKEN";
          }
        | {
              code: string;
              cleverUserInfo: {
                  cleverId: string;
                  isTeacher: boolean;
                  cleverEmail?: string;
              };
              transferToken: string;
              udiUuid: string;
              kaLocale: string | null | undefined;
          }
    >;
}

export const CLEVER_LOGIN_MUTATION: GqlOperation<
    loginWithCleverMutationType["response"],
    loginWithCleverMutationType["variables"]
> = gqlOp(gql`
    mutation loginWithCleverMutation(
        $code: String!
        $reauth: Boolean!
        $fromDevApp: Boolean!
        $isMap: Boolean!
        $isLP: Boolean!
        $isCanvas: Boolean
        $referrerUrl: String!
    ) {
        loginWithClever(
            code: $code
            reauth: $reauth
            fromDevApp: $fromDevApp
            isMap: $isMap
            isLP: $isLP
            isCanvas: $isCanvas
            referrerUrl: $referrerUrl
        ) {
            error {
                code
                cleverUserInfo {
                    cleverId
                    isTeacher
                    cleverEmail
                }
                transferToken
                udiUuid
                kaLocale
            }
            user {
                id
                kaid
                canAccessDistrictsHomepage
                isTeacher
                hasUnresolvedInvitations
                preferredKaLocale {
                    id
                    kaLocale
                    status
                }
                userDistrictInfos {
                    id
                    district {
                        id
                        kaLocale
                    }
                }
            }
            isLibraryUser
            isFirstLogin
            transferToken
        }
    }
`);

export const CLEVER_TRANSFER_AUTH_MUTATION: GqlOperation<
    transferCleverAuthMutationType["response"],
    transferCleverAuthMutationType["variables"]
> = gqlOp(gql`
    mutation transferCleverAuthMutation(
        $transferToken: String!
        $reauth: Boolean!
    ) {
        transferCleverAuth(transferToken: $transferToken, reauth: $reauth) {
            error {
                code
                cleverUserInfo {
                    cleverId
                    isTeacher
                    cleverEmail
                }
                transferToken
                udiUuid
                kaLocale
            }
            user {
                id
                kaid
                canAccessDistrictsHomepage
                isTeacher
                hasUnresolvedInvitations
                preferredKaLocale {
                    id
                    kaLocale
                    status
                }
                userDistrictInfos {
                    id
                    district {
                        id
                        kaLocale
                    }
                }
            }
            isLibraryUser
            isFirstLogin
            transferToken
        }
    }
`);

interface CleverUserInfo {
    cleverId: string;
    isTeacher: boolean;
    cleverEmail?: string;
}

interface CleverResultSuccess {
    resultType: "success";
    user: LoginUserData;
    isLibraryUser: boolean;
    transferToken: string;
}

interface CleverResultError {
    resultType: "notLinked";
    cleverUserInfo: CleverUserInfo;
    transferToken: string;
    udiUuid: string | null | undefined;
    kaLocale: string | null | undefined;
}

export type CleverResult = CleverResultSuccess | CleverResultError;

const handleCleverMutationError = (error: Error) => {
    if (error instanceof KAError) {
        throw error;
    }

    // Throw a generic error.
    throw new KAError("Failed to login ", Errors.Internal);
};

const handleCleverMutationResults = (
    resultContents?: CleverMutationData | null,
): CleverResult => {
    if (resultContents?.error) {
        if (resultContents.error.code === "ACCOUNT_NOT_LINKED") {
            // The user hasn't linked their account, but we still need their
            // Clever information so we can show the Clever onboarding UI
            return {
                resultType: "notLinked",
                cleverUserInfo: resultContents.error.cleverUserInfo,
                transferToken: resultContents.error.transferToken,
                udiUuid: resultContents.error.udiUuid,
                kaLocale: resultContents.error.kaLocale,
            };
        }
        throw new KAError(resultContents.error.code, Errors.Internal);
    }

    if (!resultContents?.user) {
        throw new KAError("clever mutation missing user data", Errors.Internal);
    }

    // We expect user to be set here.
    const user: LoginUserData = resultContents?.user;
    const isFirstLogin = resultContents?.isFirstLogin;

    // Set analytics cookies.
    if (isFirstLogin) {
        Cookies.createCookie(
            `first_login_${user.kaid}`,
            Cookies.CookieCategory.Performance,
            Date.now(),
        );
    } else {
        Cookies.createCookie(
            `returning_login_${user.kaid}`,
            Cookies.CookieCategory.Performance,
            Date.now(),
        );
    }

    return {
        resultType: "success",
        user,
        // @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
        transferToken: resultContents?.transferToken,
        // @ts-expect-error - TS2322 - Type 'boolean | undefined' is not assignable to type 'boolean'.
        isLibraryUser: resultContents?.isLibraryUser,
    };
};

/**
 * Logs the user into their clever linked account via GraphQL
 *
 * @param {CleverLoginVariables} variables The variables that will be sent to the mutation.
 * @param {string} variables.code The code that was recieved from clever, this
 * will be exchanged for an auth token so we can fetch the users information
 * @param {boolean} variables.reauth True if this is a reauth request, rather
 * than a login request
 * @param {boolean} variables.isMap True if the user is coming from the Clever
 * Map application, rather than our KAD/Clever Library application
 * @param {boolean} variables.isLP True if the user is coming from the Clever
 * LearningPaths application, rather than our KAD/Clever Library application
 * @param {string} variables.referrerUrl The URL at which the request code was provided
 *
 * @returns {Promise<CleverResult>} The mutation result.
 */
export const loginWithClever = async (
    gqlClient: ApolloGqlClient,
    variables: loginWithCleverMutationType["variables"],
): Promise<CleverResult> => {
    try {
        const result = await gqlClient.mutate(CLEVER_LOGIN_MUTATION, {
            variables,
        });
        return handleCleverMutationResults(
            // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
            result.data.loginWithClever,
        );
    } catch (error) {
        return handleCleverMutationError(error as Error);
    }
};

/**
 * Transfers a users Clever authentication from the mobile app to the web app
 *
 * @param {CleverTransferAuthVariables} variables The variables that will be sent to the mutation.
 * @param {string} variables.transferToken The transfer token that will be
 * exchanged on the server for the users Clever access code, which will
 * then be used to fetch the users data from Clever.
 * @param {boolean} variables.reauth True if this is a reauth request, rather
 * than a login request
 *
 * @returns {Promise<CleverResult>} The mutation result.
 */
export const transferCleverAuth = async (
    gqlClient: ApolloGqlClient,
    variables: transferCleverAuthMutationType["variables"],
): Promise<CleverResult> => {
    try {
        const result = await gqlClient.mutate(CLEVER_TRANSFER_AUTH_MUTATION, {
            variables,
        });
        return handleCleverMutationResults(
            // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
            result.data.transferCleverAuth,
        );
    } catch (error) {
        return handleCleverMutationError(error as Error);
    }
};
